import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { toast } from 'sonner';
import { UserPlus, Mail, Trash2, Shield } from 'lucide-react';

export default function People() {
  const { orgId } = useParams();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, [orgId]);

  const fetchMembers = async () => {
    try {
      const response = await apiClient.get(`/enterprise/orgs/${orgId}/people`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    try {
      await apiClient.post(`/enterprise/orgs/${orgId}/people/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      toast.success('Invitation sent');
      setInviteEmail('');
      setInviteRole('member');
      setInviteDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (membershipId, role) => {
    if (role === 'owner') {
      toast.error('Cannot remove owner');
      return;
    }

    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await apiClient.delete(`/enterprise/orgs/${orgId}/people/${membershipId}`);
      toast.success('Member removed');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      member: 'bg-gray-100 text-gray-700',
      billing_admin: 'bg-green-100 text-green-700',
    };
    return colors[role] || colors.member;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      invited: 'bg-warning/10 text-warning',
      disabled: 'bg-error/10 text-error',
    };
    return colors[status] || colors.active;
  };

  return (
    <div className="p-8 space-y-6" data-testid="people-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">People</h1>
          <p className="text-gray-600 mt-1">Manage organization members and invitations</p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-member-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  data-testid="invite-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  User must have a HumaTwin account
                </p>
              </div>
              <div>
                <Label>Role</Label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="billing_admin">Billing Admin</option>
                </select>
              </div>
              <Button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail}
                className="w-full"
                data-testid="send-invite-button"
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length > 0 ? (
              members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>{member.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{member.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{member.email}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${getRoleBadgeColor(member.role)}`}>
                      {member.role.replace('_', ' ')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusBadgeColor(member.status)}`}>
                      {member.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm">
                    {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'Pending'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.membershipId, member.role)}
                      disabled={member.role === 'owner'}
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  No members yet. Invite your first team member!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
